import { Suspense, useEffect, useRef, useState } from 'react'

import { createFileRoute, Link, stripSearchParams } from '@tanstack/react-router'
import clsx from 'clsx'
import z from 'zod'

import Text from '@ingka/text'

import { Footer } from '@/components/footer'
import { SkipButton } from '@/components/skip-button'
import { StickyHeader } from '@/components/sticky-header'
import { dataTestIds } from '@/constants/selectors'
import {
  Filter,
  StartGallery,
  StartGallerySkeleton,
  setFilterInSessionStorage,
  startGalleryFilterSchema,
  startGalleryFilterSchemaDefaultValues,
} from '@/features/start-gallery'
import { useRangeGalleryData } from '@/features/start-gallery/hooks/use-range-gallery-data'
import { useTranslate } from '@/hooks/use-translate'
import { getUserFlowBasedOnPlatform } from '@/integrations/insights/user-flow'
import { dexfSettingsQueryOptions } from '@/query-options/dexf-settings'
import { rangeDataQueryOptions } from '@/query-options/range-data'
import { sentinel, visuallyHidden } from '@/styles/misc/global.module.css'
import { getLocaleFromPath } from '@/utils/base-path-params'
import { getPlatform } from '@/utils/get-platform'
import { redirectHome, redirectToScene } from '@/utils/redirects'

import { atBottom, cardList, container, content, main, titleContainer } from './index.module.css'

const itemNoValidator = z.string().length(8, 'Invalid product id')

export const Route = createFileRoute('/$country/$language/navigator/')({
  validateSearch: startGalleryFilterSchema,
  component: IndexRoute,
  search: {
    middlewares: [stripSearchParams(startGalleryFilterSchemaDefaultValues)],
  },
  beforeLoad: async ({ search, context, cause }) => {
    setFilterInSessionStorage(search)
    if (cause === 'enter') {
      const platform = getPlatform(search)
      const { flowName, availableSteps } = getUserFlowBasedOnPlatform(platform)
      void context.insightsApi?.sendUserFlowStep(flowName, availableSteps.LANDING_PAGE, 1)
      context.sceneHistory.clear()
    }

    if (!search.productId) return

    const productIdResult = itemNoValidator.safeParse(search.productId)
    if (!productIdResult.success) {
      throw redirectHome()
    }

    const dexfSettings = await context.queryClient.ensureQueryData(dexfSettingsQueryOptions(getLocaleFromPath()))
    const rangeData = await context.queryClient.ensureQueryData(rangeDataQueryOptions(dexfSettings))
    const potentialSofaId = productIdResult.data
    const sofa = Object.values(rangeData)
      .flatMap((r) => r.sofas)
      .find((sofa) => sofa.itemNo === potentialSofaId || sofa.productData.content.itemNoLocal === potentialSofaId)
    if (!sofa) {
      throw redirectHome()
    }
    throw redirectToScene(sofa.rangeId, sofa.itemNo)
  },
})

function IndexRoute() {
  const { translateString } = useTranslate()
  const data = useRangeGalleryData()
  const { dev: isDev } = Route.useSearch()
  const [announcement, setAnnouncement] = useState('')
  const isInitialRender = useRef(true)

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    const timer = setTimeout(() => {
      if (data.length === 0) {
        setAnnouncement(translateString('startpage.filter.no_result'))
      } else {
        setAnnouncement(translateString('startpage.filter.nr_of_sofas').replace('{amount}', data.length.toString()))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [data.length, translateString])

  return (
    <div className={container}>
      <StickyHeader />
      <main className={main}>
        <div className={content}>
          <div className={titleContainer}>
            <Text data-testid={dataTestIds.landing.startPageIntroTitle} tagName="h1" textStyle="Display.L">
              {translateString('startpage.intro.title')}
            </Text>
            <Text tagName="p" textStyle="Body.L">
              {translateString('startpage.intro.body')}
            </Text>
            {isDev && (
              <Text tagName="p" textStyle="Body.L">
                See the current brygga stats{' '}
                <Link from="/$country/$language/navigator/" to="/$country/$language/brygga">
                  here
                </Link>
              </Text>
            )}
          </div>
          <SkipButton
            label={translateString('startpage.intro.skip_filters')}
            targetId={data.length === 0 ? 'clear-all-filters' : 'range-0'}
          />
          <Filter />
          <div className={visuallyHidden} role="status">
            {announcement}
          </div>
          <ul className={cardList} data-testid={dataTestIds.landing.startPageGallery}>
            <Suspense fallback={<StartGallerySkeleton />}>
              <StartGallery />
            </Suspense>
          </ul>
          <span aria-hidden="true" id="after-gallery-sentinel" tabIndex={-1} />
          <SkipButton
            data-testid={dataTestIds.landing.backToTopButton}
            className={clsx(sentinel, atBottom)}
            label={translateString('startpage.intro.backToTop')}
            targetId="ikea-logo"
          />
        </div>
      </main>
      <Footer />
    </div>
  )
}
